import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getBaseUrl } from '../../Utils'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../store'
import APIHelper from '../HelperMethodsForAPI'
import { VisualUnitApiDTO_VisualUnit } from '../../Interfaces/APIContracts/VisualUnitApiDTO_VisualUnit'

interface DrillthruFormState {
    loading: boolean
    successMessage?: string
    errorMessage?: string
}

export interface DrillThruDP {
    cellId: string;
    dimDPs: any[];  // Adjust the type here if you know the structure of items in dimDPs
    meId: number;
    vuDeId: number;
}

export interface IDrillthruFormData {
    dataCache: boolean;
    dashBoardId: number;
    vuId: number;
    vuTempId: number;
    cellId: string;
    cellValue: string;
    cellValueseriesName: string;
    cellValuecategoryName: string;
    pageSize: number;
    pageNumber: number;
    drillThruDP: DrillThruDP;
    drillThruActionType: string | null;
    isSummaryReport: boolean;
    MeasureListValue: string;
    CurrentClickedMeasure: string;
    ishelprequest: boolean;
    EngineType: string;
    drillThruSetId: string | number | undefined;
    vudetails_viewerType: string | undefined;
    SortColumns: string | undefined;
    drillThruSearchString: string | undefined;
    callerType: string | undefined;
    InputSearchList: any[]
}

export interface IDrillthruResponse {
    statusCode: number | string,
    data: any
}

export const drillThruFormAPI = createApi({
    reducerPath: 'drillThru', //our reducer lives at store.reducer
    tagTypes: ['drillThruFormData'],
    baseQuery: fetchBaseQuery({ baseUrl: getBaseUrl() + 'API/v3' }), //The base API path
    // The "endpoints" represent operations and requests for this server
    endpoints: (build) => ({
        // The `getPosts` endpoint is a "query" operation that returns data
        submitForm: build.mutation<IDrillthruResponse, Partial<IDrillthruFormData>>({
            query: (body) => ({
                url: '/DrillThru',
                method: 'POST',
                body,
            }),
        }),
    }),
})

interface DrillThruObjState {
    [key: string]: {
        isDrillThruOpen: boolean;
    };
}

export interface IDrillThruPayloadState {
    vuId: number;
    vuTempId: number;
    isAsync: boolean;
    cellId: string;
    cellValue: string;
    cellValueseriesName: string;
    cellValuecategoryName: string;
    vudetails: VisualUnitApiDTO_VisualUnit | null;
    pageSize: number;
    pageNumber: number;
    drillThruDP: Record<string, any>;
    isSummaryReport: boolean;
    drillThruSetId: number;
    drillThruActionType: string,
    drillThruSearchString: string,
    dashBoardId: number,
    CurrentClickedMeasure?: string,
    IsEnabledAutoNavigation?: boolean,
    MeasureListValue?: string
}

// Initial states
const initialDrillThruObjState: DrillThruObjState = {};

const initialDrillThruPayloadState: IDrillThruPayloadState = {
    vuId: -1,
    vuTempId: -1,
    isAsync: true,
    cellId: "",
    cellValue: "",
    cellValueseriesName: "",
    cellValuecategoryName: "",
    vudetails: null,
    pageSize: 10,
    pageNumber: 1,
    drillThruDP: {},
    isSummaryReport: false,
    drillThruSetId: 0,
    drillThruActionType: "dt_paginate",
    drillThruSearchString: "",
    dashBoardId: 0
};

// DrillThru_Obj Slice
const drillThruObjSlice = createSlice({
    name: 'drillThru_Obj',
    initialState: initialDrillThruObjState,
    reducers: {
        setDrillThruState: (
            state,
            action: PayloadAction<{ vuId: number; isDrillThruOpen: boolean }>
        ) => {
            const { vuId, isDrillThruOpen } = action.payload;
            state[vuId] = { isDrillThruOpen }; // Update the state for the given vuId
        },
    },
});

// DrillThruInitialPayload Slice
const drillThruInitialPayloadSlice = createSlice({
    name: 'DrillThruInitialPayload',
    initialState: initialDrillThruPayloadState,
    reducers: {
        setDrillThruPayload: (
            state,
            action: PayloadAction<IDrillThruPayloadState>
        ) => {
            return { ...state, ...action.payload };
        }
    },
});

interface IDrillThruExportPayload {
    dashBoardId: number,
    vuId: number,
    vuTempId: number
    cellId: string
    cellValue: string
    pageSize: number
    pageNumber: number
    drillThruDP: any
    drillThruSetId: any
}

interface IDrillThruSetDefaultForMeasurePayload {
    visualUnitId: number
    drillThruDP: any
    drillThruSetId: number
    oldDrillThruSetId: number
    SortColumns: any
}

interface IDrillThruExportRespData{
    rowCount: number,
    fileGuid: string
}
interface IDrillthruExportResponse {
    data: IDrillThruExportRespData,
    statusCode: number
}
interface IDrillthruSetDefaultForMeasureResponse {
    data: IDrillThruExportRespData,
    statusCode: number
}

export const drillThruActionsApi = createApi({
    reducerPath: 'exportDrillThruData', //our reducer lives at store.reducer
    tagTypes: ['exportDrillThru_api'],
    baseQuery: fetchBaseQuery({
        baseUrl: getBaseUrl() + 'API/v3',
    }), //The base API path
    // The "endpoints" represent operations and requests for this server
    endpoints: (build) => ({
        // The `getPosts` endpoint is a "query" operation that returns data
        exportDrillThru: build.mutation<IDrillthruExportResponse, Partial<IDrillThruExportPayload>>({
            query: (body) => ({
                url: '/Drillthru/export',
                method: 'POST',
                body: body
            }),
        }),
        setDrillThruDefault: build.mutation<IDrillthruSetDefaultForMeasureResponse, Partial<IDrillThruSetDefaultForMeasurePayload>>({
            query: (body) => ({
                url: '/Drillthru/SetDrillThruSetDefaultForMeasure',
                method: 'PUT',
                body: APIHelper.prepareFormData(body)
            }),
        }),
    }),
})

export const { setDrillThruState } = drillThruObjSlice.actions;
export const { setDrillThruPayload } = drillThruInitialPayloadSlice.actions;
export const getDrillThruPayload = (state: RootState) => state.DrillThruInitialPayload
export const selectDrillThruStateByVuId = (state: any, vuId: number) => state.drillThru_Obj[vuId];
export const drillThruObjReducer = drillThruObjSlice.reducer;
export const drillThruInitialPayloadReducer = drillThruInitialPayloadSlice.reducer;

export const { useSubmitFormMutation } = drillThruFormAPI
export const { useExportDrillThruMutation } = drillThruActionsApi
export const { useSetDrillThruDefaultMutation } = drillThruActionsApi
